import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  setPreview,
  setLoading,
  setError,
  resetPreviewState,
} from "../../store/slices/qztray/qzTrayPreviewSlice";
import QzTrayPreviewService from "../../services/qztray/QzTrayPreviewService";
import useApiErrorHandler from "../useApiErrorHandler";
import useAlerts from "../useAlerts";

/**
 * 👁️ Hook: useQzTrayPreview
 *
 * Gestiona la obtención y generación de la vista previa del ticket
 * antes de enviarlo a impresión con QZ Tray.
 *
 * ✅ Funcionalidades:
 * - Obtener una vista previa de ejemplo (demo)
 * - Generar una vista previa real desde datos
 * - Manejo de estados (loading / error / data)
 * - Alertas visuales consistentes
 */
export const useQzTrayPreview = () => {
  const dispatch = useDispatch();
  const { preview, isLoading, error } = useSelector(
    (state: RootState) => state.qzTrayPreview
  );

  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();

  // ========================================================
  // 🎨 Obtener vista previa de ejemplo
  // ========================================================
  const fetchSamplePreview = async () => {
    try {
      dispatch(setLoading(true));
      const response = await QzTrayPreviewService.getSamplePreview();

      const { statusCode, data, message } = response;
      if (statusCode === 200 && data) {
        dispatch(setPreview(data));
        showAlert("success", "Vista previa generada", message || "Ticket de ejemplo generado correctamente.");
      } else {
        dispatch(setError("No se pudo generar la vista previa de ejemplo."));
        showAlert("warning", "Advertencia", "No se encontró vista previa válida.");
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("Error al generar la vista previa de ejemplo."));
      showAlert("error", "Error", "No se pudo obtener la vista previa de ejemplo.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  // ========================================================
  // 🧾 Generar vista previa real desde datos
  // ========================================================
  const generatePreview = async (payload: any) => {
    try {
      dispatch(setLoading(true));
      const response = await QzTrayPreviewService.generatePreview(payload);

      const { statusCode, data, message } = response;
      if (statusCode === 200 && data) {
        dispatch(setPreview(data));
        showAlert("success", "Vista previa generada", message || "Vista previa real creada correctamente.");
      } else {
        dispatch(setError("No se pudo generar la vista previa real."));
        showAlert("warning", "Advertencia", "La respuesta del servidor fue incompleta.");
      }
    } catch (error: any) {
      handle(error);
      dispatch(setError("Error al generar la vista previa real."));
      showAlert("error", "Error", "Ocurrió un problema al generar la vista previa.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  // ========================================================
  // 🧹 Limpiar vista previa actual
  // ========================================================
  const clearPreview = () => {
    dispatch(resetPreviewState());
  };

  // ========================================================
  // 🧩 Retorno del hook
  // ========================================================
  return {
    preview,
    isLoading,
    error,
    fetchSamplePreview,
    generatePreview,
    clearPreview,
  };
};
